import React, { useState } from 'react';
import BillList from '../components/bills/BillList.tsx';
import BillForm from '../components/bills/BillForm.tsx';
import { useBills } from '../hooks/useBills.ts';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import GenerateBillsModal from '../components/bills/GenerateBillsModal.tsx';
import '../styles/Bills.css';

const Bills: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const { bills, loading, error, updateBill, refresh } = useBills();

  const handleFormClose = async (shouldRefresh: boolean = false) => {
    setIsFormOpen(false);
    if (shouldRefresh) {
      await refresh(); // Only refresh if explicitly requested
    }
  };

  const handleDateChange = (field: 'from' | 'to') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    setDateRange(prev => {
      // If setting 'from' date and 'to' date exists but is earlier than new 'from' date
      if (field === 'from' && prev.to && prev.to < newValue) {
        return {
          from: newValue,
          to: newValue // Reset 'to' date to match 'from' date
        };
      }
      return {
        ...prev,
        [field]: newValue
      };
    });
  };

  const handleClearFilters = () => {
    setDateRange({ from: '', to: '' });
    setSelectedStatus('all');
  };
  
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error.message} />;

  return (
    <div className="bills-page">
      <div className="bills-header bill-filters">
        <h1>Bills</h1>
        <div className="bills-actions">
          <button className="btn-primary" onClick={() => setIsFormOpen(true)}>
            Create Bill
          </button>
          <button className="btn-secondary" onClick={() => setShowGenerateModal(true)}>
            Generate Bills
          </button>
        </div>
      </div>

      <div className="bill-filters">
        <div className="filters-header">
          <h3>Filter Bills</h3>
          {(dateRange.from || dateRange.to || selectedStatus !== 'all') && (
            <button 
              className="clear-filters-btn" 
              onClick={handleClearFilters}
              title="Clear filters"
            >
              ×
            </button>
          )}
        </div>

        <div className="filter-group">
          <label>From Date:</label>
          <input
            type="date"
            value={dateRange.from}
            onChange={handleDateChange('from')}
          />
        </div>

        <div className="filter-group">
          <label>To Date:</label>
          <input
            type="date"
            value={dateRange.to}
            min={dateRange.from || undefined}
            onChange={handleDateChange('to')}
            disabled={!dateRange.from} // Disable until 'from' date is selected
          />
        </div>

        <div className="filter-group">
          <label>Filter by Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="Paid">Paid</option>
            <option value="Unpaid">Unpaid</option>
            <option value="Partial">Partial</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <BillList
        bills={bills}
        onUpdate={updateBill}
        onRefresh={refresh}
        dateRange={dateRange}
        statusFilter={selectedStatus}
      />

      {isFormOpen && (
        <BillForm
          onClose={handleFormClose}
        />
      )}

      {showGenerateModal && (
        <GenerateBillsModal
          onClose={() => setShowGenerateModal(false)}
          onSuccess={refresh}
        />
      )}
    </div>
  );
};

export default Bills;
